import { ScopeButton } from '@monorepo/shared-lib';
import { Form, Space } from 'antd';
import { FormTextArea } from 'components/Custom/inputs';
import { MessageStates } from 'containers/MessageBox';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnDrawerOff, turnMessageOn } from 'store/app/actions';
import type { MessageData } from 'store/app/types';
import { editOrganisationsRequest } from 'store/organisation/actions';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import {
  isOrganisationNotesWithinLimit,
  ORGANISATION_NOTES_MAX_LENGTH,
} from './organisation-notes-limits';

export { ORGANISATION_NOTES_MAX_LENGTH } from './organisation-notes-limits';

type EntityProfileFormValues = {
  coreBusinessRevenue: string;
  directCostsOverhead: string;
  geographicTaxFootprint: string;
  travelEmployeeExpenses: string;
  departmentalTracking: string;
};

type EntityProfileQuestionKey = keyof EntityProfileFormValues;

const ENTITY_PROFILE_QUESTION_KEYS: EntityProfileQuestionKey[] = [
  'coreBusinessRevenue',
  'directCostsOverhead',
  'geographicTaxFootprint',
  'travelEmployeeExpenses',
  'departmentalTracking',
];

/** English section titles used in persisted notes (must stay stable for parsing). */
const PERSISTED_SECTION_TITLES: Record<EntityProfileQuestionKey, string> = {
  coreBusinessRevenue: 'Core Business & Revenue',
  directCostsOverhead: 'Direct Costs vs. Overhead',
  geographicTaxFootprint: 'Geographic & Tax Footprint',
  travelEmployeeExpenses: 'Travel & Employee Expenses',
  departmentalTracking: 'Departmental Tracking',
};

/** Legacy persisted format (no longer written); still stripped on save. */
const LEGACY_ENTITY_PROFILE_BLOCK =
  /\n?={3}\s*ENTITY PROFILE\s*={3}\n[\s\S]*?\n={3}\s*\/ENTITY PROFILE\s*={3}\n?/g;

/**
 * Matches the saved profile block without delimiter lines: title + 5 subtitle/answer pairs.
 */
const ENTITY_PROFILE_BLOCK_REGEX =
  /(?:^|\n\n)Entity Profile\n\nCore Business & Revenue\n[\s\S]*?\n\nDirect Costs vs\. Overhead\n[\s\S]*?\n\nGeographic & Tax Footprint\n[\s\S]*?\n\nTravel & Employee Expenses\n[\s\S]*?\n\nDepartmental Tracking\n[\s\S]*?(?=\n\n|$)/g;

export function buildEntityProfileNotes(
  values: EntityProfileFormValues
): string {
  const qas = ENTITY_PROFILE_QUESTION_KEYS.map((name) => ({
    subtitle: PERSISTED_SECTION_TITLES[name],
    answer: values[name],
  }));

  const body = qas
    .map(({ subtitle, answer }) => `${subtitle}\n${answer}`)
    .join('\n\n');

  return `Entity Profile\n\n${body}`;
}

function stripEntityProfileSections(notes: string): string {
  return notes
    .replace(LEGACY_ENTITY_PROFILE_BLOCK, '\n')
    .replace(ENTITY_PROFILE_BLOCK_REGEX, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function upsertEntityProfileSection(
  existingNotes: string | undefined,
  entityProfileSection: string
): string {
  const withoutProfile = stripEntityProfileSections(existingNotes ?? '');

  if (!withoutProfile) return entityProfileSection;
  return `${withoutProfile}\n\n${entityProfileSection}`;
}

export function hasEntityProfileSection(
  existingNotes: string | undefined
): boolean {
  if (!existingNotes?.trim()) return false;
  if (existingNotes.includes('=== ENTITY PROFILE ===')) return true;
  return (
    existingNotes.includes('Entity Profile\n\nCore Business & Revenue') ||
    existingNotes.includes('Entity Profile\r\n\r\nCore Business & Revenue')
  );
}

export const EntityProfileDrawerContent: React.FC<{
  organisationId: string;
  existingNotes?: string;
}> = ({ organisationId, existingNotes }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm<EntityProfileFormValues>();
  const pwaMobileNav = usePwaMobileNavEnabled();

  return (
    <div
      className={pwaMobileNav ? 'scope-entity-profile-drawer' : undefined}
      style={{ padding: '0 8px' }}
    >
      <style>
        {`
          /* Force consistent textarea height for entity profile drawer */
          .entity-profile-drawer-textarea.ant-input {
            height: 100px !important;
            min-height: 100px !important;
            max-height: 100px !important;
            resize: none !important;
            width: 100% !important;
          }
        `}
      </style>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => {
          const profileNotesSection = buildEntityProfileNotes(values);
          const nextNotes = upsertEntityProfileSection(
            existingNotes,
            profileNotesSection
          );

          if (!isOrganisationNotesWithinLimit(nextNotes)) {
            dispatch(
              turnMessageOn({
                title: t('organisation.entity-profile-notes-too-long-title'),
                description: t('organisation.notes-too-long-description', {
                  max: ORGANISATION_NOTES_MAX_LENGTH,
                  count: nextNotes.length,
                }),
                type: MessageStates.WARNING,
              } as MessageData)
            );
            return;
          }

          dispatch(
            editOrganisationsRequest({
              refId: organisationId,
              notes: nextNotes,
            })
          );
          dispatch(turnDrawerOff());
        }}
      >
        <div
          className={
            pwaMobileNav ? 'scope-entity-profile-drawer__questions' : undefined
          }
          style={{
            display: 'grid',
            gap: 24,
            overflow: 'auto',
            maxHeight: pwaMobileNav
              ? undefined
              : 'calc(100vh - 225px)',
            scrollbarWidth: 'thin',
            paddingRight: 12,
          }}
        >
          {ENTITY_PROFILE_QUESTION_KEYS.map((name, idx) => (
            <div key={name}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 6,
                  fontSize: 12,
                  color: 'var(--color-text-base, #042a2a)',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  {idx + 1}.{' '}
                  {t(`organisation.entity-profile-questions.${name}.label`)}
                </span>
              </div>

              <Form.Item
                name={name}
                rules={[
                  { required: true, message: t('common.required-field') },
                ]}
                style={{ marginBottom: 0 }}
              >
                <FormTextArea
                  rows={2}
                  maxLength={2000}
                  placeholder={t(
                    `organisation.entity-profile-questions.${name}.placeholder`
                  )}
                  className="entity-profile-drawer-textarea"
                />
              </Form.Item>

              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: 'var(--color-text-muted, #5a6a6a)',
                  lineHeight: 1.4,
                }}
              >
                <span style={{ fontWeight: 600, color: 'var(--color-text-muted, #5a6a6a)' }}>
                  {t('organisation.entity-profile-tip')}
                </span>{' '}
                {t(`organisation.entity-profile-questions.${name}.tip`)}
              </div>
            </div>
          ))}
        </div>

        <div
          className={
            pwaMobileNav ? 'scope-entity-profile-drawer__footer' : undefined
          }
          style={{ marginTop: pwaMobileNav ? 16 : 40 }}
        >
          <Space style={{ width: '100%', justifyContent: 'center' }}>
            <ScopeButton
              type="default"
              width="140px"
              height="40px"
              onClick={() => dispatch(turnDrawerOff())}
            >
              {t('common.cancel')}
            </ScopeButton>
            <ScopeButton
              type="primary"
              width="160px"
              height="40px"
              onClick={() => form.submit()}
            >
              {t('organisation.entity-profile-save')}
            </ScopeButton>
          </Space>
        </div>
        {!pwaMobileNav ? <div style={{ height: 30 }} /> : null}
      </Form>
    </div>
  );
};
